import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import { Box, FormGroup, FormControlLabel, Switch } from "@mui/material";
import { Contact } from "@repo/server-api";
import { WorkingConditionInformation } from "@repo/server-api/types";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, {
	useState,
	useEffect,
	forwardRef,
	useRef,
	useCallback,
	useImperativeHandle,
} from "react";

import DateField from "../fields/DateField";
import NumberTextField from "../fields/NumberTextField";

const ExternalWorkingConditions = forwardRef(
	(
		props: Readonly<{
			contact: Contact | null;
			setContact: (value: Contact | null) => void;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const [info, setInfo] = useState<WorkingConditionInformation>({
			isFlexible: false,
			startDate: "",
			hoursPerWeek: 0,
			hoursPerMonday: 0,
			hoursPerTuesday: 0,
			hoursPerWednesday: 0,
			hoursPerThursday: 0,
			hoursPerFriday: 0,
			hoursPerSaturday: 0,
			hoursPerSunday: 0,
			id: "",
			__typename: "WorkingConditionInformation",
		});

		const [isLoaded, setIsLoaded] = useState<boolean>(false);

		const startDateRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);
		const hoursPerWeekRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);
		const hoursPerMondayRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);
		const hoursPerTuesdayRef = useRef<{
			validate: () => Promise<Record<string, string>>;
		} | null>(null);
		const hoursPerWednesdayRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);
		const hoursPerThursdayRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);
		const hoursPerFridayRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);
		const hoursPerSaturdayRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);
		const hoursPerSundayRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};

			localErrors.startDate = (await startDateRef.current?.validate()) || {};

			if (!info.isFlexible) {
				localErrors.hoursPerMonday =
					(await hoursPerMondayRef.current?.validate()) || {};
				localErrors.hoursPerTuesday =
					(await hoursPerTuesdayRef.current?.validate()) || {};
				localErrors.hoursPerWednesday =
					(await hoursPerWednesdayRef.current?.validate()) || {};
				localErrors.hoursPerThursday =
					(await hoursPerThursdayRef.current?.validate()) || {};
				localErrors.hoursPerFriday =
					(await hoursPerFridayRef.current?.validate()) || {};
				localErrors.hoursPerSaturday =
					(await hoursPerSaturdayRef.current?.validate()) || {};
				localErrors.hoursPerSunday =
					(await hoursPerSundayRef.current?.validate()) || {};
			} else {
				localErrors.hoursPerWeek =
					(await hoursPerWeekRef.current?.validate()) || {};
			}

			props.setErrors?.(localErrors);

			return localErrors;
		}, [info, props]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		useEffect(() => {
			if (props.contact && !isLoaded) {
				setIsLoaded(true);
				setInfo(
					props.contact.additionalInformation.filter(
						(info): info is WorkingConditionInformation =>
							(info as WorkingConditionInformation).__typename ===
							"WorkingConditionInformation",
					)[0] ?? {
						id: "",
						__typename: "WorkingConditionInformation",
						isFlexible: false,
						startDate: "",
						hoursPerWeek: 0,
						hoursPerMonday: 0,
						hoursPerTuesday: 0,
						hoursPerWednesday: 0,
						hoursPerThursday: 0,
						hoursPerFriday: 0,
						hoursPerSaturday: 0,
						hoursPerSunday: 0,
					},
				);
			}
		}, [props.contact, isLoaded]);

		const haveWorkingConditions = (contact: Contact | null): boolean => {
			return contact
				? contact.additionalInformation.some(
						(i) =>
							(i as WorkingConditionInformation).__typename ===
							"WorkingConditionInformation",
					)
				: false;
		};

		useEffect(() => {
			if (props.contact && isLoaded) {
				props.setContact({
					...props.contact,
					id: props.contact.id ?? "",
					additionalInformation: haveWorkingConditions(props.contact)
						? props.contact.additionalInformation.map((i) =>
								(i as WorkingConditionInformation).__typename ===
								"WorkingConditionInformation"
									? { ...info }
									: i,
							)
						: [...props.contact.additionalInformation, info],
				});
			}
			 
		}, [info]);

		return (
			<div>
				<Box className="fullCenter" sx={{ mb: 3 }}>
					<FormGroup className="padding7">
						<FormControlLabel
							control={
								<Switch
									onChange={(event) => {
										setInfo({ ...info, isFlexible: event.target.checked });
									}}
									checked={info.isFlexible}
								/>
							}
							label={t("workingCondition.isFlexible")}
						/>
					</FormGroup>
					<DateField
						isLoaded={isLoaded}
						ref={startDateRef}
						value={info.startDate ?? ""}
						setValue={(value) =>
							setInfo({
								...info,
								startDate: dayjs(value, "YYYY-MM-DD").format("YYYY-MM-DD"),
							})
						}
						label={t("workingCondition.startDate")}
						errors={props.errors?.startDate as Record<string, string>}
						setErrors={(value) =>
							props.setErrors &&
							props.setErrors({ ...props.errors, startDate: value })
						}
					/>
				</Box>
				{info.isFlexible ? (
					<Box className="fullCenter">
						<Box sx={{ width: "45%" }}>
							<NumberTextField
								isLoaded={isLoaded}
								ref={hoursPerWeekRef}
								value={info.hoursPerWeek}
								setValue={(value) => {
									setInfo({ ...info, hoursPerWeek: value ?? 0 });
								}}
								label={t("workingCondition.hoursPerWeek")}
								errors={props.errors?.hoursPerWeek as Record<string, string>}
								setErrors={(value) =>
									props.setErrors &&
									props.setErrors({ ...props.errors, hoursPerWeek: value })
								}
								icon={<AccessAlarmIcon />}
							/>
						</Box>
					</Box>
				) : (
					<div>
						<Box className="fullCenter">
							<Box sx={{ width: "45%" }}>
								<NumberTextField
									isLoaded={isLoaded}
									ref={hoursPerMondayRef}
									value={info.hoursPerMonday}
									setValue={(value) => {
										setInfo({ ...info, hoursPerMonday: value ?? 0 });
									}}
									label={t("workingCondition.hoursPerMonday")}
									errors={
										props.errors?.hoursPerMonday as Record<string, string>
									}
									setErrors={(value) =>
										props.setErrors &&
										props.setErrors({ ...props.errors, hoursPerMonday: value })
									}
									icon={<AccessAlarmIcon />}
								/>
							</Box>
							<Box sx={{ width: "45%" }}>
								<NumberTextField
									isLoaded={isLoaded}
									ref={hoursPerTuesdayRef}
									value={info.hoursPerTuesday}
									setValue={(value) => {
										setInfo({ ...info, hoursPerTuesday: value ?? 0 });
									}}
									label={t("workingCondition.hoursPerTuesday")}
									errors={
										props.errors?.hoursPerTuesday as Record<string, string>
									}
									setErrors={(value) =>
										props.setErrors &&
										props.setErrors({ ...props.errors, hoursPerTuesday: value })
									}
									icon={<AccessAlarmIcon />}
								/>
							</Box>
						</Box>
						<Box className="fullCenter">
							<Box sx={{ width: "45%" }}>
								<NumberTextField
									isLoaded={isLoaded}
									ref={hoursPerWednesdayRef}
									value={info.hoursPerWednesday}
									setValue={(value) => {
										setInfo({ ...info, hoursPerWednesday: value ?? 0 });
									}}
									label={t("workingCondition.hoursPerWednesday")}
									errors={
										props.errors?.hoursPerWednesday as Record<string, string>
									}
									setErrors={(value) =>
										props.setErrors &&
										props.setErrors({
											...props.errors,
											hoursPerWednesday: value,
										})
									}
									icon={<AccessAlarmIcon />}
								/>
							</Box>
							<Box sx={{ width: "45%" }}>
								<NumberTextField
									isLoaded={isLoaded}
									ref={hoursPerThursdayRef}
									value={info.hoursPerThursday}
									setValue={(value) => {
										setInfo({ ...info, hoursPerThursday: value ?? 0 });
									}}
									label={t("workingCondition.hoursPerThursday")}
									errors={
										props.errors?.hoursPerThursday as Record<string, string>
									}
									setErrors={(value) =>
										props.setErrors &&
										props.setErrors({
											...props.errors,
											hoursPerThursday: value,
										})
									}
									icon={<AccessAlarmIcon />}
								/>
							</Box>
						</Box>
						<Box className="fullCenter">
							<Box sx={{ width: "45%" }}>
								<NumberTextField
									isLoaded={isLoaded}
									ref={hoursPerFridayRef}
									value={info.hoursPerFriday}
									setValue={(value) => {
										setInfo({ ...info, hoursPerFriday: value ?? 0 });
									}}
									label={t("workingCondition.hoursPerFriday")}
									errors={
										props.errors?.hoursPerFriday as Record<string, string>
									}
									setErrors={(value) =>
										props.setErrors &&
										props.setErrors({ ...props.errors, hoursPerFriday: value })
									}
									icon={<AccessAlarmIcon />}
								/>
							</Box>
							<Box sx={{ width: "45%" }}>
								<NumberTextField
									isLoaded={isLoaded}
									ref={hoursPerSaturdayRef}
									value={info.hoursPerSaturday}
									setValue={(value) => {
										setInfo({ ...info, hoursPerSaturday: value ?? 0 });
									}}
									label={t("workingCondition.hoursPerSaturday")}
									errors={
										props.errors?.hoursPerSaturday as Record<string, string>
									}
									setErrors={(value) =>
										props.setErrors &&
										props.setErrors({
											...props.errors,
											hoursPerSaturday: value,
										})
									}
									icon={<AccessAlarmIcon />}
								/>
							</Box>
						</Box>
						<Box className="fullCenter">
							<Box sx={{ width: "45%" }}>
								<NumberTextField
									isLoaded={isLoaded}
									ref={hoursPerSundayRef}
									value={info.hoursPerSunday}
									setValue={(value) => {
										setInfo({ ...info, hoursPerSunday: value ?? 0 });
									}}
									label={t("workingCondition.hoursPerSunday")}
									errors={
										props.errors?.hoursPerSunday as Record<string, string>
									}
									setErrors={(value) =>
										props.setErrors &&
										props.setErrors({ ...props.errors, hoursPerSunday: value })
									}
									icon={<AccessAlarmIcon />}
								/>
							</Box>
						</Box>
					</div>
				)}
			</div>
		);
	},
);

ExternalWorkingConditions.displayName = "ExternalWorkingConditions";
export default ExternalWorkingConditions;
